import {
  type ComponentProps,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { StreamdownContext } from 'streamdown';
import { cn } from '@/utils/cn';
import { useT } from '@/utils/i18n';
import { CheckmarkSFSymbolRegular } from '@/assets/sf-symbols/regular/checkmark';
import { DocumentOnDocumentSFSymbolRegular } from '@/assets/sf-symbols/regular/document.on.document';
import { useCodeBlockContext } from './context';

export type CodeBlockCopyButtonProps = ComponentProps<'button'> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};

export const CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  code: propCode,
  ...props
}: CodeBlockCopyButtonProps & { code?: string }) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef(0);
  const t = useT();
  const { code: contextCode } = useCodeBlockContext();
  const { isAnimating } = useContext(StreamdownContext);
  const code = propCode ?? contextCode;

  const copyToClipboard = async () => {
    if (typeof window === 'undefined' || !navigator?.clipboard?.writeText) {
      onError?.(new Error('Clipboard API not available'));
      return;
    }

    try {
      if (!isCopied) {
        await navigator.clipboard.writeText(code);
        setIsCopied(true);
        onCopy?.();
        window.jsb?.MessagesBridge?.trackCopyFromChat('code_block');
        timeoutRef.current = window.setTimeout(
          () => setIsCopied(false),
          timeout
        );
      }
    } catch (error) {
      onError?.(error as Error);
    }
  };

  useEffect(
    () => () => {
      window.clearTimeout(timeoutRef.current);
    },
    []
  );

  const Icon = isCopied
    ? CheckmarkSFSymbolRegular
    : DocumentOnDocumentSFSymbolRegular;

  return (
    <button
      className={cn('icon-button size-5.5 flex-center opacity-85', className)}
      data-streamdown="code-block-copy-button"
      disabled={isAnimating}
      onClick={copyToClipboard}
      title={t('Copy Code')}
      type="button"
      {...props}
    >
      {children ?? (
        <Icon className={cn(isCopied ? 'text-[0.625rem]' : 'text-base')} />
      )}
    </button>
  );
};
